/**
 * A MakeHuman person who isn't you: the same parametric body and clothes as the player (so they can wear
 * anything in the wardrobe), with a little life of their own: stand, play a clip (dance, cheer), walk to a point.
 * Heavier than a stock crowd avatar, so they're for the few people a scene is about (the bottle girls, the
 * dancers on the podiums), not for crowds.
 */
import * as THREE from 'three';
import { characterBody } from './character';
import { DEFAULT_APPEARANCE, type Appearance } from './model';
import type { PlayerBody } from '../player';
import type { BackrestStop } from '../experience/backrest';

export class Npc {
  readonly root: THREE.Object3D;
  private mixer: THREE.AnimationMixer;
  private idle: THREE.AnimationAction;
  private walkA: THREE.AnimationAction;
  private act: THREE.AnimationAction | null = null;
  private actW = 0;
  private old: THREE.AnimationAction | null = null;
  private target: { to: THREE.Vector3; done: () => void } | null = null;
  private walking = 0;
  /** runs after the animation each frame: a scene can bend the pose (leaning in) */
  afterPose: (() => void) | null = null;
  /** driven from outside (another player over the network): their walk/run weights instead of walkTo's */
  stride: { move: number; run: number } | null = null;
  /** sitting against something solid: the sway rests against it instead of passing through */
  backrest: BackrestStop | null = null;
  private constructor(private body: PlayerBody) {
    this.root = body.root;
    this.root.traverse((o) => { if ((o as THREE.Mesh).isMesh) { o.castShadow = false; o.frustumCulled = false; } }); // (skinned shadows are the expensive part)
    this.mixer = new THREE.AnimationMixer(this.root);
    this.idle = this.mixer.clipAction(body.idle).play();
    this.walkA = this.mixer.clipAction(body.walk).play();
    this.walkA.setEffectiveWeight(0);
  }

  /** `lite`: another player's body (smaller pictures, shared with the other players') */
  /** this body and its sit clip, to seat it somewhere (a friend at the wheel of the car you're in) */
  seatable(): { root: THREE.Object3D; sit: THREE.AnimationClip } | null { return this.body.sit ? { root: this.root, sit: this.body.sit } : null; }
  static async create(look: Partial<Appearance>, renderer: THREE.WebGLRenderer, o: { lite?: boolean } = {}): Promise<Npc> {
    const body = await characterBody({ ...DEFAULT_APPEARANCE, ...look, macros: { ...DEFAULT_APPEARANCE.macros, ...look.macros } }, renderer, o);
    return new Npc(body);
  }

  /** once their skin and clothes are on (for a photo) */
  textured() { return this.body.textured?.() ?? Promise.resolve(); }

  place(p: THREE.Vector3, face: THREE.Vector3) {
    this.root.position.copy(p);
    this.root.rotation.y = Math.atan2(face.x - p.x, face.z - p.z);
    this.backrest?.rebase();
  }

  /** play a clip in place ('dance', 'cheer'…; null: just stand) */
  async play(name: string | null, from = Math.random(), snap = false) {
    if (!name) { this.old?.stop(); this.old = this.act; this.act = null; this.backrest = null; return; }
    const c = await this.body.extra?.(name);
    if (!c) return;
    const a = this.mixer.clipAction(c);
    if (a === this.act) return;
    this.old?.stop();
    this.old = this.act; // (fades out below while the new one fades in)
    a.reset().play();
    a.time = from * c.duration;
    a.timeScale = 0.92 + Math.random() * 0.16;
    a.setEffectiveWeight(snap ? 1 : 0);
    this.act = a;
    this.actW = snap ? 1 : 0;
    if (snap) { this.old?.stop(); this.old = null; }
  }

  /** walk to a point (in a straight line: the scene keeps the way clear); resolves on arrival */
  walkTo(to: THREE.Vector3): Promise<void> {
    void this.play(null);
    return new Promise((done) => { this.target = { to: to.clone(), done }; });
  }

  update(dt: number) {
    const t = this.target;
    this.walking = THREE.MathUtils.damp(this.walking, this.stride ? this.stride.move : t ? 1 : 0, 8, dt);
    if (this.stride) this.walkA.timeScale = 1 + this.stride.run * 0.45;
    if (t) {
      const d = new THREE.Vector3(t.to.x - this.root.position.x, 0, t.to.z - this.root.position.z);
      const left = d.length();
      if (left < 0.06) { this.target = null; t.done(); }
      else {
        const want = Math.atan2(d.x, d.z);
        let r = want - this.root.rotation.y; r = Math.atan2(Math.sin(r), Math.cos(r));
        this.root.rotation.y += r * Math.min(1, dt * 8);
        this.root.position.addScaledVector(d.normalize(), Math.min(left, this.body.walkSpeed * this.walking * dt));
      }
    }
    // weights by hand (crossFadeTo disables what it fades out): the clip over standing, walking over both
    this.actW = THREE.MathUtils.damp(this.actW, this.act ? 1 : 0, 7, dt);
    const still = 1 - this.walking;
    this.walkA.setEffectiveWeight(this.walking);
    this.act?.setEffectiveWeight(still * this.actW);
    if (this.old) { const w = (1 - this.actW) * still; this.old.setEffectiveWeight(this.act ? w : still * this.actW); if (this.act ? w < 0.01 : this.actW < 0.01) { this.old.stop(); this.old = null; } }
    // standing fills in only what the clips don't: none while one clip hands over to the next
    this.idle.setEffectiveWeight(still * (this.act ? (this.old ? 0 : 1 - this.actW) : this.old ? 1 - this.actW : 1));
    this.mixer.update(dt);
    this.backrest?.apply();
    this.afterPose?.();
  }

  dispose() {
    this.mixer.stopAllAction();
    this.root.removeFromParent();
    // everything it put on the GPU: meshes, their materials and textures (its body, hair, clothes), the skeleton's bones
    // (a texture someone else still uses just uploads again: three re-creates a disposed one on its next draw)
    const mats = new Set<THREE.Material>();
    this.root.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh) return;
      m.geometry.dispose();
      for (const x of Array.isArray(m.material) ? m.material : [m.material]) mats.add(x);
      if ((o as THREE.SkinnedMesh).isSkinnedMesh) (o as THREE.SkinnedMesh).skeleton.dispose();
    });
    for (const m of mats) {
      for (const v of Object.values(m)) if ((v as THREE.Texture | null)?.isTexture && !(v as THREE.Texture).userData.shared) (v as THREE.Texture).dispose(); // (a shared one is the cache's to free)
      m.dispose();
    }
    this.body.free?.();
  }
}
